import React from 'react';

/** Bannière d'erreur — affichée dans les pages en cas d'échec. */
export const ErrorBanner: React.FC<{ message: string; onRetry?: () => void; onDismiss?: () => void }> = ({ message, onRetry, onDismiss }) => {
  if (!message) return null;
  return (
    <div style={{
      background: 'rgba(255,68,68,0.07)',
      border: '1px solid rgba(255,68,68,0.25)',
      borderRadius: 14,
      padding: '14px 16px',
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: '#FF9999', lineHeight: 1.5 }}>{message}</span>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: '6px 14px', background: 'rgba(255,68,68,0.12)', border: '1px solid rgba(255,68,68,0.3)', borderRadius: 8, color: '#FF8888', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Réessayer
        </button>
      )}
      {onDismiss && (
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#FF8888', fontSize: 16, cursor: 'pointer', padding: 4 }}>✕</button>
      )}
    </div>
  );
};

/** Overlay de chargement plein écran avec étape en cours. */
export const LoadingOverlay: React.FC<{ label?: string; step?: string; progress?: number }> = ({ label = 'Génération en cours', step, progress }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,12,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1500, padding: 24 }}>
    <div style={{ width: 46, height: 46, border: '3px solid rgba(108,71,255,0.2)', borderTopColor: '#8B6FFF', borderRadius: '50%', animation: 'uispin 0.9s linear infinite', marginBottom: 18 }} />
    <div style={{ fontSize: 17, fontWeight: 700, color: '#F8F7FF', marginBottom: 6, textAlign: 'center' }}>{label}</div>
    {step && <div style={{ fontSize: 13, color: '#9898B8', textAlign: 'center', maxWidth: 320 }}>{step}</div>}
    {typeof progress === 'number' && (
      <div style={{ width: '100%', maxWidth: 320, marginTop: 20 }}>
        <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(progress, 100)}%`, background: 'linear-gradient(90deg,#6C47FF,#00F5A0)', borderRadius: 3, transition: 'width 0.4s' }} />
        </div>
        <div style={{ textAlign: 'right', fontSize: 11, color: '#8B6FFF', fontWeight: 700, marginTop: 5 }}>{Math.round(progress)}%</div>
      </div>
    )}
    <style>{'@keyframes uispin { to { transform: rotate(360deg); } }'}</style>
  </div>
);

/** État vide — quand il n'y a rien à afficher. */
export const EmptyState: React.FC<{ icon?: string; title: string; description?: string; action?: React.ReactNode }> = ({ icon = '📭', title, description, action }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#6C6C8A' }}>
    <div style={{ fontSize: 42, marginBottom: 14 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 600, color: '#C8C8E8', marginBottom: 6 }}>{title}</div>
    {description && <div style={{ fontSize: 13, maxWidth: 380, margin: '0 auto 16px', lineHeight: 1.6 }}>{description}</div>}
    {action}
  </div>
);

/** Toast en bas à droite. */
export const Toast: React.FC<{ message: string; type?: 'success' | 'error' }> = ({ message, type = 'success' }) => {
  const color = type === 'error' ? '#FF8888' : '#00F5A0';
  const border = type === 'error' ? 'rgba(255,68,68,0.35)' : 'rgba(0,245,160,0.3)';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, left: 'auto',
      maxWidth: 'calc(100vw - 48px)',
      background: '#12121F', border: `1px solid ${border}`, borderRadius: 12,
      padding: '12px 20px', color, zIndex: 2000, fontSize: 13, fontWeight: 500,
      opacity: message ? 1 : 0,
      transform: message ? 'translateY(0)' : 'translateY(8px)',
      transition: 'all 0.3s', pointerEvents: 'none',
    }}>
      {message}
    </div>
  );
};
